import api from './api';

// AppData Interfaces - alle nullable für maximale Robustheit
export interface Club {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  logo?: string;
  images?: string[];
  address?: Address;
  phone?: string;
  email?: string;
  website?: string;
  categories?: string[];
  tags?: string[];
  verified?: boolean;
  visible?: boolean;
  sponsored?: boolean;
  edart?: boolean;
  steeldart?: boolean;
  views?: number;
  createdAt?: number;
  updatedAt?: number;
}

export interface Pub {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  logo?: string;
  images?: string[];
  address?: Address;
  phone?: string;
  email?: string;
  website?: string;
  categories?: string[];
  tags?: string[];
  boards?: number;
  verified?: boolean;
  visible?: boolean;
  sponsored?: boolean;
  edart?: boolean;
  steeldart?: boolean;
  flat?: boolean;
  rentable?: boolean;
  views?: number;
  createdAt?: number;
  updatedAt?: number;
}

export interface Shop {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  logo?: string;
  images?: string[];
  address?: Address;
  phone?: string;
  email?: string;
  website?: string;
  categories?: string[];
  tags?: string[];
  verified?: boolean;
  visible?: boolean;
  sponsored?: boolean;
  shopLink?: string;
  views?: number;
  createdAt?: number;
  updatedAt?: number;
}

export interface Series {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  images?: string[];
  address?: Address;
  phone?: string;
  email?: string;
  categories?: string[];
  tags?: string[];
  tournaments?: string[];
  verified?: boolean;
  visible?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

export interface Address {
  street?: string;
  houseNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  label?: string;
}

export interface AppDataStats {
  users?: number;
  tournaments?: number;
  clubs?: number;
  pubs?: number;
  shops?: number;
  series?: number;
}

export interface AppData {
  // Listings
  clubs?: Club[];
  pubs?: Pub[];
  shops?: Shop[];
  series?: Series[];
  tournaments?: any[]; // Already defined in tournamentService
  
  // Meta data
  countries?: any[];
  languages?: any[];
  translations?: any;
  tournamentTags?: string[];
  seriesTags?: string[];
  
  // Stats
  stats?: AppDataStats;
  server?: {
    timestamp?: number;
    version?: string;
    cachedAt?: number;
  };
}

class AppDataService {
  private cachedAppData: AppData | null = null;
  private cacheTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten Cache

  // Load complete app data (cached)
  async getAppData(slim: boolean = true): Promise<AppData> {
    // Check cache
    if (this.cachedAppData && Date.now() - this.cacheTime < this.CACHE_DURATION) {
      return this.cachedAppData;
    }

    try {
      const response = await api.get(`/api/appdata?slim=${slim}`);
      this.cachedAppData = response.data || {};
      this.cacheTime = Date.now();
      return this.cachedAppData!;
    } catch (error) {
      console.error('Fehler beim Laden der AppData:', error);
      return this.cachedAppData || {};
    }
  }

  // Get clubs only
  async getClubs(): Promise<Club[]> {
    const appData = await this.getAppData();
    return this.getSafeClubArray(appData.clubs);
  }

  // Get pubs only  
  async getPubs(): Promise<Pub[]> {
    const appData = await this.getAppData();
    return this.getSafePubArray(appData.pubs);
  }

  // Get shops only
  async getShops(): Promise<Shop[]> {
    const appData = await this.getAppData();
    return this.getSafeShopArray(appData.shops);
  }

  // Get series only
  async getSeries(): Promise<Series[]> {
    const appData = await this.getAppData();
    return this.getSafeSeriesArray(appData.series);
  }

  // Get app statistics
  async getStats(): Promise<AppDataStats> {
    const appData = await this.getAppData();
    return appData.stats || {};
  }

  // Helper: Safe array access for clubs
  getSafeClubArray(data?: Club[]): Club[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(club => club && typeof club === 'object' && club._id);
  }

  // Helper: Safe array access for pubs
  getSafePubArray(data?: Pub[]): Pub[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(pub => pub && typeof pub === 'object' && pub._id);
  }

  // Helper: Safe array access for shops
  getSafeShopArray(data?: Shop[]): Shop[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(shop => shop && typeof shop === 'object' && shop._id);
  }

  // Helper: Safe array access for series
  getSafeSeriesArray(data?: Series[]): Series[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(series => series && typeof series === 'object' && series._id);
  }

  // Helper: Get image URL (robust)
  getImageUrl(item?: Club | Pub | Shop | Series): string | null {
    if (!item) return null;

    // Priority: logo > image > images[0]
    if ('logo' in item) {
      const logoUrl = this.buildFileUrl((item as Club).logo);
      if (logoUrl) return logoUrl;
    }

    const imageUrl = this.buildFileUrl(item.image);
    if (imageUrl) return imageUrl;

    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      return this.buildFileUrl(item.images[0]);
    }

    return null;
  }

  // Helper: vollständige Datei-URL bauen
  // Backend liefert i. d. R. nur Datei-IDs → über /api/file/<id> ausliefern
  buildFileUrl(fileName?: string | null): string | null {
    if (!fileName || typeof fileName !== 'string' || !fileName.trim()) return null;
    const path = fileName.trim();
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return `${api.defaults.baseURL}${path}`;
    return `${api.defaults.baseURL}/api/file/${path}`;
  }

  // Helper: Banner-Bild (image > images[0], OHNE Logo)
  getBannerUrl(item?: Club | Pub | Shop | Series): string | null {
    if (!item) return null;
    if (item.image) return this.buildFileUrl(item.image);
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      return this.buildFileUrl(item.images[0]);
    }
    return null;
  }

  // Helper: Logo-Bild (nur logo)
  getLogoUrl(item?: Club | Pub | Shop): string | null {
    if (!item || !('logo' in item)) return null;
    return this.buildFileUrl((item as Club).logo);
  }

  // Helper: Format address (robust)
  formatAddress(address?: Address): string {
    if (!address || typeof address !== 'object') return 'Adresse unbekannt';
    
    // Check for label first (often complete address from backend)
    if (address.label && typeof address.label === 'string' && address.label.trim()) {
      return address.label.trim();
    }
    
    const parts: string[] = [];
    
    try {
      if (address.street && typeof address.street === 'string' && address.street.trim()) {
        let streetPart = address.street.trim();
        if (address.houseNumber && typeof address.houseNumber === 'string' && address.houseNumber.trim()) {
          streetPart += ` ${address.houseNumber.trim()}`;
        }
        parts.push(streetPart);
      }
      
      if (address.zipCode && address.city && 
          typeof address.zipCode === 'string' && typeof address.city === 'string' &&
          address.zipCode.trim() && address.city.trim()) {
        parts.push(`${address.zipCode.trim()} ${address.city.trim()}`);
      } else if (address.city && typeof address.city === 'string' && address.city.trim()) {
        parts.push(address.city.trim());
      }
      
      if (parts.length === 0) {
        return address.country && typeof address.country === 'string' && address.country.trim() 
          ? address.country.trim() 
          : 'Adresse unbekannt';
      }
      
      return parts.join(', ');
    } catch (error) {
      return 'Adresse unbekannt';
    }
  }

  // Helper: Format name (robust)
  formatName(item?: Club | Pub | Shop | Series): string {
    if (!item) return 'Name nicht verfügbar';
    if (item.name && typeof item.name === 'string' && item.name.trim()) {
      return item.name.trim();
    }
    return 'Kein Name';
  }

  // Clear cache manually
  clearCache(): void {
    this.cachedAppData = null;
    this.cacheTime = 0;
  }
}

export default new AppDataService();